import React from 'react';
interface LogoProps {
  /** rendered height of the logo in pixels (width scales automatically) */
  size?: number;
  className?: string;
  /** hide the "BARBEROS" wordmark + tagline, showing only the emblem */
  emblemOnly?: boolean;
}
/**
 * BarberOS full brand logo — an original SVG re-creation of the uploaded
 * artwork (metallic-silver lettering, warm-gold crown & ring, ringed "B"
 * monogram with a straight razor). Fully vector, so it scales crisply and
 * carries no pasted-image edges. Colors are sampled to match the source art.
 */
export function Logo({
  size = 200,
  className = '',
  emblemOnly = false
}: LogoProps) {
  // Full lockup is ~ 300 x 300 in viewBox units; emblem-only is 300 x 210.
  const vbHeight = emblemOnly ? 210 : 300;
  const ratio = 300 / vbHeight;
  return <svg width={size * ratio} height={size} viewBox={`0 0 300 ${vbHeight}`} fill="none" role="img" aria-label="BarberOS" className={className} data-id="element-160">
      <defs data-id="element-161">
        {/* Metallic silver (chrome) gradient */}
        <linearGradient id="bo-silver" x1="0" y1="0" x2="0" y2="1" data-id="element-162">
          <stop offset="0%" stopColor="#fdfdfd" data-id="element-163" />
          <stop offset="28%" stopColor="#d6d7db" data-id="element-164" />
          <stop offset="52%" stopColor="#8f9196" data-id="element-165" />
          <stop offset="53%" stopColor="#c8cace" data-id="element-166" />
          <stop offset="100%" stopColor="#6f7176" data-id="element-167" />
        </linearGradient>
        {/* Warm gold gradient */}
        <linearGradient id="bo-gold" x1="0" y1="0" x2="0" y2="1" data-id="element-168">
          <stop offset="0%" stopColor="#f3d98a" data-id="element-169" />
          <stop offset="45%" stopColor="#d9b55a" data-id="element-170" />
          <stop offset="70%" stopColor="#b5883a" data-id="element-171" />
          <stop offset="100%" stopColor="#8a6522" data-id="element-172" />
        </linearGradient>
        <radialGradient id="bo-glow" cx="50%" cy="42%" r="55%" data-id="element-173">
          <stop offset="0%" stopColor="rgba(217,181,90,0.18)" data-id="element-174" />
          <stop offset="100%" stopColor="rgba(217,181,90,0)" data-id="element-175" />
        </radialGradient>
      </defs>

      {/* Ambient glow */}
      <rect x="0" y="0" width="300" height={vbHeight} fill="url(#bo-glow)" data-id="element-176" />

      {/* --- CROWN --- */}
      <g fill="url(#bo-gold)" data-id="element-177">
        <path d="M124 34 L138 52 L150 26 L162 52 L176 34 L172 66 L128 66 Z" data-id="element-178" />
        <circle cx="150" cy="20" r="5" data-id="element-179" />
        <rect x="126" y="68" width="48" height="5" rx="2" data-id="element-180" />
      </g>

      {/* --- OUTER RINGS --- */}
      <circle cx="150" cy="118" r="60" stroke="url(#bo-gold)" strokeWidth="2" fill="none" data-id="element-181" />
      <circle cx="150" cy="118" r="66" stroke="url(#bo-gold)" strokeWidth="1" opacity="0.5" fill="none" data-id="element-182" />

      {/* EST. / 2024 flanking text */}
      <text x="72" y="122" fontSize="11" fontFamily="Poppins, sans-serif" fontWeight="600" letterSpacing="1" textAnchor="middle" fill="url(#bo-gold)" data-id="element-183">
        EST.
      </text>
      <text x="228" y="122" fontSize="11" fontFamily="Poppins, sans-serif" fontWeight="600" letterSpacing="1" textAnchor="middle" fill="url(#bo-gold)" data-id="element-184">
        2024
      </text>

      {/* --- MONOGRAM B --- */}
      <text x="150" y="132" fontSize="96" fontFamily="Georgia, 'Times New Roman', serif" fontWeight="700" textAnchor="middle" dominantBaseline="middle" fill="url(#bo-silver)" data-id="element-185">
        B
      </text>

      {/* --- STRAIGHT RAZOR crossing the monogram --- */}
      <g stroke="url(#bo-silver)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-186">
        {/* blade */}
        <path d="M112 150 L176 96" data-id="element-187" />
        {/* handle */}
        <path d="M176 96 L196 84" data-id="element-188" />
        {/* pivot pins */}
      </g>
      <circle cx="180" cy="93" r="3.5" fill="url(#bo-silver)" data-id="element-189" />
      <circle cx="112" cy="150" r="4" fill="url(#bo-silver)" data-id="element-190" />

      {!emblemOnly && <>
          {/* --- WORDMARK --- */}
          <text x="150" y="238" fontSize="46" fontFamily="Georgia, 'Times New Roman', serif" fontWeight="700" letterSpacing="2" textAnchor="middle" fill="url(#bo-silver)" data-id="element-191">
            BARBEROS
          </text>
          {/* --- TAGLINE --- */}
          <text x="150" y="266" fontSize="11" fontFamily="Poppins, sans-serif" fontWeight="500" letterSpacing="3" textAnchor="middle" fill="url(#bo-gold)" data-id="element-192">
            TU ESTILO. NUESTRA PASIÓN.
          </text>
          {/* small diamond flourish under tagline */}
          <path d="M150 278 l5 5 l-5 5 l-5 -5 Z" fill="url(#bo-gold)" data-id="element-193" />
        </>}
    </svg>;
}
interface LogoMarkProps {
  /** pixel size of the square mark */
  size?: number;
  className?: string;
  /** stroke color (defaults to currentColor so it inherits text color) */
  color?: string;
}
/**
 * BarberOS outlined emblem — a compact, line-drawn version of the brand mark
 * recreated as an SVG (crown + ringed "B" monogram + straight-razor accent).
 * Renders crisply at any size, so it works both as a page accent and inside
 * the small chatbot circle. Uses strokes only, so it reads as an outline.
 */
export function LogoMark({
  size = 28,
  className = '',
  color = 'currentColor'
}: LogoMarkProps) {
  return <svg width={size} height={size} viewBox="0 0 64 64" fill="none" role="img" aria-label="BarberOS" className={className} data-id="element-194">
      <g stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-195">
        {/* Crown */}
        <path d="M24 12 L28 17 L32 10 L36 17 L40 12 L39 20 L25 20 Z" data-id="element-196" />
        {/* Outer ring */}
        <circle cx="32" cy="38" r="19" opacity="0.9" data-id="element-197" />
        {/* Inner ring accent */}
        <circle cx="32" cy="38" r="15" opacity="0.35" data-id="element-198" />
        {/* Straight razor accent across the emblem */}
        <path d="M20 48 L40 30" opacity="0.7" data-id="element-199" />
        <path d="M40 30 L45 28 L43 33 Z" opacity="0.7" data-id="element-200" />
      </g>
      {/* Monogram B */}
      <text x="32" y="43" fontSize="22" fontFamily="Poppins, serif" fontWeight="700" textAnchor="middle" dominantBaseline="middle" fill={color} data-id="element-201">
        B
      </text>
    </svg>;
}